import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { IPerfil, ISelectOption, ITamanhoBarra, IPerfilComponente } from "../types/perfil.types";
import { RetornoMidia, RetornoNomeMidia, RetornoRemocaoMidia } from "../../types/midia.types";

// ===== CRUD de Perfil =====

export const obterPerfilPorId = async (idPerfil: number): Promise<IPerfil | null> => {
  try {
    const { data } = await api.get(`/PerfilN1/ObterPorId/${idPerfil}`);

    // FASE 05 Ajustes — PO 2026-08-22 (item 3, cobra a decisão de 10/08): a fonte dos tamanhos
    // é a tabela legada PerfisBarras (todos os perfis existentes têm as barras lá). Barra1..3
    // ficam como fallback para perfis salvos pelo MOF novo antes desta correção.
    let tamanhoBarras: ITamanhoBarra[] = await obterTamanhosBarras(idPerfil);
    if (tamanhoBarras.length === 0) {
      if (data.barra1 && data.barra1 > 0) {
        tamanhoBarras.push({ sequencia: 1, comprimento: data.barra1 });
      }
      if (data.barra2 && data.barra2 > 0) {
        tamanhoBarras.push({ sequencia: 2, comprimento: data.barra2 });
      }
      if (data.barra3 && data.barra3 > 0) {
        tamanhoBarras.push({ sequencia: 3, comprimento: data.barra3 });
      }
    }
    
    // FASE 05 — 6ª validação/PO 2026-08-10: o "Tipo do Perfil" do form vem de PerfilComposto
    // (0=Cor Única,1=Interno,2=Externo,3=Composto), NÃO de data.tipoPerfil (que é a sigla do
    // Prenativo). Sem este mapeamento o campo perdia a seleção ao recarregar após salvar.
    const mapaComposto: Record<number, string> = { 0: "cor_unica", 1: "interno", 2: "externo", 3: "composto" };

    return {
      ...data,
      tipoPerfil: mapaComposto[data.perfilComposto ?? 0] ?? "cor_unica",
      tamanhoBarras,
      // FASE 05 — 2ª validação/PO 2026-07-24 (F15): o backend retorna "peso"; o form usa
      // "pesoKgPorMetro". Sem este mapeamento o Peso não aparecia ao consultar.
      pesoKgPorMetro: data.peso,
      // FASE 05 — 2ª validação/PO 2026-07-24 (F10): "Considerar Perda" (Sim/Não) é o inverso
      // do dado legado DesconsiderarPerdaBarra. Default (nulo) = Sim (considera perda).
      considerarPerda: data.desconsiderarPerdaBarra !== true,
      // Mapear campos de mídia
      iconeUrl: data.icone,
      iconeNomeArquivo: data.iconeNomeArquivo || undefined,
      figuraUrl: data.imagem,
      figuraNomeArquivo: data.imagemNomeArquivo || undefined,
      desenhoTecnicoUrl: data.desenhoTecnico,
      desenhoTecnicoNomeArquivo: data.desenhoTecnicoNomeArquivo || undefined,
      composicao: [], // Composição ainda não implementada
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter perfil");
    return null;
  }
};

export const salvarPerfil = async (perfil: Partial<IPerfil>): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const isEdicao = !!perfil.idPerfil;
    const endpoint = isEdicao ? `/PerfilN1/Atualizar/${perfil.idPerfil}` : '/PerfilN1/Criar';
    const method = isEdicao ? 'put' : 'post';
    
    // Limpar valores inválidos antes de enviar
    const dadosLimpos: any = { ...perfil };

    // FASE 05 — 2ª validação/PO 2026-07-24 (F15): o backend espera "Peso" (o form usa
    // pesoKgPorMetro). Sem este mapeamento o Peso nunca era gravado (chegava 0).
    dadosLimpos.peso = perfil.pesoKgPorMetro ?? 0;
    // FASE 05 — 2ª validação/PO 2026-07-24 (F10): "Considerar Perda" grava no campo legado
    // invertido DesconsiderarPerdaBarra (Sim → false, Não → true; default Sim).
    dadosLimpos.desconsiderarPerdaBarra = perfil.considerarPerda === false;

    // FASE 05 Ajustes — PO 2026-08-22 (item 3): PerfisBarras é a fonte da verdade — TODAS as
    // barras são gravadas via POST TamanhosBarras após o salvar (não há mais teto de 3; antes
    // o slice(0,3) descartava da 4ª em diante em silêncio). Barra1..3 seguem gravadas como
    // colunas DERIVADAS das 3 primeiras, por compatibilidade com o legado (que exporta Barra1).
    const barrasParaGravar: ITamanhoBarra[] = (Array.isArray(dadosLimpos.tamanhoBarras) ? dadosLimpos.tamanhoBarras : [])
      .filter((b: ITamanhoBarra) => b.comprimento > 0)
      .sort((a: ITamanhoBarra, b: ITamanhoBarra) => a.sequencia - b.sequencia);

    dadosLimpos.barra1 = barrasParaGravar[0]?.comprimento || 0;
    dadosLimpos.barra2 = barrasParaGravar[1]?.comprimento || 0;
    dadosLimpos.barra3 = barrasParaGravar[2]?.comprimento || 0;

    // Remover tamanhoBarras do payload (não é esperado pelo endpoint de salvar)
    delete dadosLimpos.tamanhoBarras;
    
    // Remover composicao do payload (não implementado ainda no backend)
    delete dadosLimpos.composicao;
    
    // Remover campos numéricos com valor 0 ou inválido (exceto se forem obrigatórios)
    if (dadosLimpos.barra1 === 0 || dadosLimpos.barra1 === null || dadosLimpos.barra1 === undefined) {
      dadosLimpos.barra1 = 0; // Manter 0 como padrão (campo obrigatório no banco)
    }
    if (dadosLimpos.barra2 === 0 || dadosLimpos.barra2 === null || dadosLimpos.barra2 === undefined) {
      dadosLimpos.barra2 = 0; // Manter 0 como padrão (campo obrigatório no banco)
    }
    if (dadosLimpos.barra3 === 0 || dadosLimpos.barra3 === null || dadosLimpos.barra3 === undefined) {
      dadosLimpos.barra3 = 0; // Manter 0 como padrão (campo obrigatório no banco)
    }
    if (!dadosLimpos.idReferencia || dadosLimpos.idReferencia === 0) {
      delete dadosLimpos.idReferencia;
    }
    if (!dadosLimpos.idLinha || dadosLimpos.idLinha === 0) {
      delete dadosLimpos.idLinha;
    }
    if (!dadosLimpos.idClasseId || dadosLimpos.idClasseId === 0) {
      delete dadosLimpos.idClasseId;
    }
    if (!dadosLimpos.idPrenativo || dadosLimpos.idPrenativo === 0) {
      delete dadosLimpos.idPrenativo;
    }
    // FASE 05 — persistência 2026-08-10: o "Tipo do Perfil" (cor_unica/interno/externo/composto)
    // grava em PerfilComposto (0/1/2/3), NÃO em TipoPerfil (que passa a ser a sigla do Prenativo,
    // resolvida no backend).
    const mapaComposto: Record<string, number> = { cor_unica: 0, interno: 1, externo: 2, composto: 3 };
    dadosLimpos.perfilComposto = mapaComposto[(dadosLimpos.tipoPerfil ?? "cor_unica") as string] ?? 0;
    
    const { data } = await api[method](endpoint, dadosLimpos);

    // Grava as barras em PerfisBarras no id RETORNADO (o espelho publicado pode ter roteado o
    // salvar para a cópia de trabalho — mesmo cuidado das cores de Componente/Vidro).
    const idParaBarras = data?.id || perfil.idPerfil;
    if (data?.sucesso !== false && idParaBarras) {
      await salvarTamanhosBarras(idParaBarras, barrasParaGravar);
    }

    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar perfil");
    return null;
  }
};

// ===== Tamanhos de barra (tabela legada PerfisBarras — item 3 dos Ajustes 22/08) =====

export const obterTamanhosBarras = async (idPerfil: number): Promise<ITamanhoBarra[]> => {
  try {
    const { data } = await api.get(`/PerfilN1/${idPerfil}/TamanhosBarras`);
    // O backend devolve { tamanhos: [{ sequencia, comprimento }] } — a sequência vem do
    // IdPerfilBarras; a tela só precisa de uma ordem estável, renumerada aqui.
    return (data?.tamanhos || []).map((t: any, i: number) => ({
      sequencia: i + 1,
      comprimento: t.comprimento,
    }));
  } catch (error: any) {
    console.error("Erro ao obter tamanhos de barras:", error?.response?.status);
    return [];
  }
};

export const salvarTamanhosBarras = async (idPerfil: number, tamanhos: ITamanhoBarra[]): Promise<boolean> => {
  try {
    await api.post(`/PerfilN1/${idPerfil}/TamanhosBarras`, {
      tamanhos: tamanhos.map(t => ({ sequencia: t.sequencia, comprimento: t.comprimento })),
    });
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar tamanhos de barras");
    return false;
  }
};

export const excluirPerfil = async (idPerfil: number): Promise<boolean> => {
  try {
    await api.delete(`/PerfilN1/Excluir/${idPerfil}`);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao excluir perfil");
    return false;
  }
};

export const duplicarPerfil = async (idPerfil: number): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const { data } = await api.post(`/PerfilN1/Duplicar/${idPerfil}`);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao duplicar perfil");
    return null;
  }
};

export const validarCodigoUnico = async (codigo: string, idExcluir?: number): Promise<boolean> => {
  try {
    const params: any = { codigo };
    if (idExcluir) params.idExcluir = idExcluir;
    
    const { data } = await api.get('/PerfilN1/ValidarCodigoUnico', { params });
    return data.disponivel;
  } catch (error: any) {
    console.error("Erro ao validar código:", error);
    return false;
  }
};

// ===== Listas de Referência =====
// Usa o novo controller ReferenciaDadosController que não exige permissão "Configuracao"

export const listarReferencias = async (idTipoTela?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idTipoTela) params.idTipoTela = idTipoTela;
    const { data } = await api.get('/ReferenciaDados/Referencias', { params });
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.idReferencia || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar referências:", error);
    return [];
  }
};

export const listarLinhas = async (idReferencia?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idReferencia) params.idReferencia = idReferencia;
    
    const { data } = await api.get('/ReferenciaDados/Linhas', { params });
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.idLinha || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar linhas:", error);
    return [];
  }
};

export const listarLigas = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/Ligas');
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.idLiga || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar ligas:", error);
    return [];
  }
};

export const listarTemperas = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/Temperas');
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.id,
      nome: item.nome || item.descricao,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar temperas:", error);
    return [];
  }
};

export const listarPrenativos = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/Prenativos');
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.idPrenativo || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar prenativos:", error);
    return [];
  }
};

// FASE 05 — S3/PO 2026-05-13: lista prenativos vinculados a uma Linha
// (via LinhasPrenativos). Carregada dinamicamente quando o usuario seleciona
// a Linha no formulario de cadastro de Perfil.
export const listarPrenativosPorLinha = async (idLinha: number): Promise<ISelectOption[]> => {
  try {
    if (!idLinha) return [];
    const { data } = await api.get('/ReferenciaDados/Prenativos/PorLinha', { params: { idLinha } });
    return (data || []).map((item: any) => ({
      id: item.idPrenativo || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar prenativos por linha:", error);
    return [];
  }
};

export const listarClasseIds = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/ClasseIds');
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.idClasseId || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar ClasseIds:", error);
    return [];
  }
};

export const listarUnidadesValor = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/UnidadesValor');
    // Mapear para o formato esperado pelo frontend
    return (data || []).map((item: any) => ({
      id: item.idUnidadeItemEmbalagem || item.id,
      nome: item.descricao || item.nome,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao listar unidades de valor:", error);
    return [];
  }
};

// ===== Tamanhos de Barras =====

export const salvarTamanhosBarra = async (idPerfil: number, tamanhos: ITamanhoBarra[]): Promise<boolean> => {
  try {
    await api.post(`/PerfilN1/${idPerfil}/TamanhosBarras`, { tamanhos });
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar tamanhos de barras");
    return false;
  }
};

// ===== Composição =====

export const salvarComposicao = async (idPerfil: number, composicao: IPerfilComponente[]): Promise<boolean> => {
  try {
    await api.post(`/PerfilN1/${idPerfil}/Composicao`, { composicao });
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar composição");
    return false;
  }
};

export const obterComposicao = async (idPerfil: number): Promise<IPerfilComponente[] | null> => {
  try {
    const { data } = await api.get(`/PerfilN1/${idPerfil}/Composicao`);
    // Mapear resposta do backend para IPerfilComponente
    const items = data?.items || [];
    return items.map((item: any, index: number) => ({
      sequencia: item.sequencia || index + 1,
      codigoComponente: item.codigoComponente || "",
      descricaoComponente: item.descricaoComponente || "",
      tipoPerfilComponente: (item.tipoPerfilComponente || "cor_unica") as 'cor_unica' | 'interno' | 'externo',
      idPerfilComponente: item.idPerfilComponente
    }));
  } catch (error: any) {
    // Se o endpoint retornar 404, significa que não há composição ainda
    if (error.response?.status === 404) {
      return [];
    }
    getErrorsByApi(error, "Erro ao obter composição");
    return null;
  }
};

export const buscarPerfisParaComposicao = async (termo: string): Promise<Array<{ idPerfil: number; codigo: string; descricao: string; tipoPerfil: string; perfilComposto: number }>> => {
  try {
    // FASE 05 — 6ª validação/PO 2026-08-07: não filtrar por tipo. O filtro 'interno,externo'
    // escondia os perfis legado/base (TipoPerfil 'S'/'T'), mostrando só os recém-cadastrados no
    // MOF. A Composição deve listar TODOS os códigos das referências liberadas para a empresa.
    const { data } = await api.get('/PerfilN1/BuscarPorCodigo', {
      params: { termo }
    });
    // Mapear PerfilBuscaDTO para formato esperado pelo frontend
    return (data?.items || []).map((item: any) => ({
      idPerfil: item.idPerfil,
      codigo: item.codigo,
      descricao: item.descricao,
      tipoPerfil: item.tipoPerfil,
      perfilComposto: item.perfilComposto ?? 0
    }));
  } catch (error: any) {
    console.error("Erro ao buscar perfis para composição:", error);
    return [];
  }
};

// ===== Upload de Mídias =====

// Plano 21/09/2026 (Frente 3): as escritas de mídia devolvem também o `id` do registro gravado —
// com o produto Disponível, a API grava na cópia de trabalho e a tela precisa seguir para ela.
export const uploadIcone = async (idPerfil: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);

    const { data } = await api.post(`/PerfilN1/${idPerfil}/Midias/UploadIcone`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data?.url ? { url: data.url, id: data.id ?? idPerfil } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload do ícone");
    return null;
  }
};

export const uploadFigura = async (idPerfil: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);

    const { data } = await api.post(`/PerfilN1/${idPerfil}/Midias/UploadFigura`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data?.url ? { url: data.url, id: data.id ?? idPerfil } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da figura");
    // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 3): re-lança para o modal não exibir
    // "Mídias salvas com sucesso" quando a figura não subiu.
    throw error;
  }
};

export const uploadDesenhoTecnico = async (idPerfil: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);

    const { data } = await api.post(`/PerfilN1/${idPerfil}/Midias/UploadDesenhoTecnico`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data?.url ? { url: data.url, id: data.id ?? idPerfil } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload do desenho técnico");
    return null;
  }
};

export const removerMidia = async (idPerfil: number, tipoMidia: 'icone' | 'figura' | 'desenhoTecnico'): Promise<RetornoRemocaoMidia | null> => {
  try {
    const endpoints: Record<string, string> = {
      icone: `/PerfilN1/${idPerfil}/Midias/RemoverIcone`,
      figura: `/PerfilN1/${idPerfil}/Midias/RemoverFigura`,
      desenhoTecnico: `/PerfilN1/${idPerfil}/Midias/RemoverDesenhoTecnico`,
    };

    const { data } = await api.delete(endpoints[tipoMidia]);
    return { sucesso: true, id: data?.id ?? idPerfil };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover mídia");
    return null;
  }
};

// ===== Histórico =====

export interface IHistoricoPerfil {
  dataHora: string;
  usuario: string;
  acao: string;
  camposAlterados?: string[];
}

export interface IHistoricoAtualizacao {
  id: number;
  dataAlteracao: Date;
  usuario: string;
  campo: string;
  valorAnterior: string;
  valorNovo: string;
}

export const obterHistoricoPerfil = async (idPerfil: number): Promise<IHistoricoPerfil[]> => {
  try {
    const { data } = await api.get(`/PerfilN1/${idPerfil}/Historico`);
    return data?.items || data || [];
  } catch (error: any) {
    console.error("Erro ao obter histórico:", error);
    return [];
  }
};

export const obterHistoricoAtualizacoes = async (idPerfil: number): Promise<IHistoricoAtualizacao[]> => {
  try {
    const { data } = await api.get(`/PerfilN1/${idPerfil}/HistoricoAtualizacoes`);
    return data?.items || data || [];
  } catch (error: any) {
    console.error("Erro ao obter histórico de atualizações:", error);
    return [];
  }
};

// ===== Definir apenas o NOME do arquivo (sem upload) =====

/**
 * Reporte de 31/08 (item 6): grava só o nome informado, sem enviar arquivo — é assim que o
 * produto assume uma mídia que já foi carregada por outro produto da empresa (F05A-P3b).
 * Devolve `arquivoExiste` para a tela avisar quando o nome ainda não tem arquivo no acervo.
 */
export const definirNomeMidiaPerfil = async (
  idPerfil: number,
  tipoMidia: "icone" | "figura" | "desenhoTecnico",
  nomeArquivo: string
): Promise<RetornoNomeMidia | null> => {
  try {
    const { data } = await api.put(`/PerfilN1/${idPerfil}/Midias/DefinirNome`, {
      tipoMidia,
      nomeArquivo,
    });
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao gravar o nome do arquivo");
    return null;
  }
};
